import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import Products from './products';
import Header from './header';
import Footer from './Footer';
import ProductModal from './ProductModal';
import styles from './CategoryProducts.module.css';

const CategoryProducts = ({ cartItems = 0, onAddToCart, cartList = [], cartTotal = 0, showCart = false, onCartToggle = () => {}, onRemoveItem = () => {} }) => {
    // Used for /category/:category, /size/:size and /trolly routes
    const { category, size } = useParams();
    const isTrolly = useLocation().pathname === '/trolly';
    const itemsQuery = isTrolly
        ? 'trolly=yes'
        : size
            ? `size=${encodeURIComponent(size)}`
            : `category=${encodeURIComponent(category)}`;
    const pageTitle = isTrolly ? 'Trolley' : size ? `${size} cases` : category;
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);

    const [filterVisible, setFilterVisible] = useState(false);
    const filterRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (filterRef.current && !filterRef.current.contains(event.target)) {
                setFilterVisible(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const fetchProductsByCategory = async () => {
            setLoading(true);
            try {
                const response = await axios.get(`/items?${itemsQuery}`);
                // Ensure response.data is an array
                if (Array.isArray(response.data)) {
                    setProducts(response.data);
                } else {
                    console.error('Expected array but got:', response.data);
                    setProducts([]);
                }
            } catch (err) {
                setError(err.message);
                console.error('Error fetching products by category:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchProductsByCategory();
    }, [itemsQuery]);

const handleItemClick = async (product) => {
        setModalLoading(true);
        try {
            const response = await axios.get(`/albums/${product.item_id}`);
            const photos = response.data.length > 0
                ? response.data.map(album => album.photo_url.startsWith('/') ? album.photo_url : '/' + album.photo_url)
                : [product.photo_url, product.photo_url, product.photo_url].map(url => url.startsWith('/') ? url : '/' + url); // fallback

            setSelectedProduct({ ...product, photos });
        } catch (error) {
            console.error('Error fetching album photos:', error);
            const photos = product.photos && product.photos.length > 0
                ? product.photos.map(url => url.startsWith('/') ? url : '/' + url)
                : [product.photo_url, product.photo_url, product.photo_url].map(url => url.startsWith('/') ? url : '/' + url); // fallback

            setSelectedProduct({ ...product, photos });
        } finally {
            setModalLoading(false);
        }
    };

    const closeModal = () => {
        setSelectedProduct(null);
    };

    const [minPrice, setMinPrice] = React.useState(0);
    const [maxPrice, setMaxPrice] = React.useState(1000);
    const [maxAvailablePrice, setMaxAvailablePrice] = React.useState(1000);
    const [brands, setBrands] = React.useState([]);
    const [selectedBrands, setSelectedBrands] = React.useState([]);

    useEffect(() => {
        const fetchProductsByCategory = async () => {
            setLoading(true);
            try {
                const url = `/items?${itemsQuery}`;
                const response = await axios.get(url);
                let filteredProducts = Array.isArray(response.data) ? response.data : [];
                // Extract unique brands
                const uniqueBrands = [...new Set(filteredProducts.map(p => p.brand).filter(Boolean))];
                setBrands(uniqueBrands);
                // Update maxAvailablePrice based on fetched products
                const prices = filteredProducts.map(p => p.sell_price);
                const maxPriceInData = prices.length > 0 ? Math.max(...prices) : 1000;
                setMaxAvailablePrice(maxPriceInData);
                // Clamp maxPrice if needed
                if (maxPrice > maxPriceInData) {
                    setMaxPrice(maxPriceInData);
                }
                if (minPrice < 0) {
                    setMinPrice(0);
                }
                if (minPrice !== '') {
                    filteredProducts = filteredProducts.filter(p => p.sell_price >= minPrice);
                }
                if (maxPrice !== '') {
                    filteredProducts = filteredProducts.filter(p => p.sell_price <= maxPrice);
                }
                if (selectedBrands.length > 0) {
                    filteredProducts = filteredProducts.filter(p => selectedBrands.includes(p.brand));
                }
                setProducts(filteredProducts);
            } catch (err) {
                setError(err.message);
                console.error('Error fetching products by category:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchProductsByCategory();
    }, [itemsQuery, minPrice, maxPrice, selectedBrands]);

    const [currentPage, setCurrentPage] = React.useState(1);
    const itemsPerPage = 20;

    // Calculate total pages
    const totalPages = Math.ceil(products.length / itemsPerPage);

    // Get products for current page
    const pagedProducts = Array.isArray(products) ? products.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage) : [];

    const goToPage = (page) => {
        if (page < 1) page = 1;
        else if (page > totalPages) page = totalPages;
        setCurrentPage(page);
    };

    return (
        <>
            <Header 
                cartItems={cartItems}
                cartList={cartList.map(item => ({
                    ...item,
                    photo_url: item.photo_url.startsWith('/') ? item.photo_url : '/' + item.photo_url
                }))}
                cartTotal={cartTotal}
                showCartDropdown={showCart}
                onCartToggle={onCartToggle}
                onRemoveItem={onRemoveItem}
            />

            <br/>   <br/>   <br/>   <br/>

            <div className={styles.categoryContainer} style={{ paddingTop: '120px' }}>
                <div className={styles.pageHeader}>
                    <h1 className={styles.categoryTitle}>
                        {pageTitle}
                    </h1>
                    <p className={styles.categorySubtitle}>
                        Discover our  collection of {pageTitle.toLowerCase()} products
                    </p>
                </div>

                <button
                    onClick={() => setFilterVisible(!filterVisible)}
                    className={styles.filterToggle}
                >
                    {filterVisible ? 'Hide Filters' : 'Show Filters'}
                </button>

                <div className={styles.contentWrapper}>
                    <div
                        ref={filterRef}
                        className={`${styles.filterSidebar} ${!filterVisible ? styles.hidden : ''}`}
                    >
                        <div className={styles.filterSection}>
                            <h3 className={styles.filterTitle}>Filter by Price</h3>
                            <div className={styles.priceRangeContainer}>
                                <label htmlFor="minPrice" className={styles.priceLabel}>
                                    Min Price: <span className={styles.priceValue}>${minPrice}</span>
                                </label>
                                <input
                                    type="range"
                                    id="minPrice"
                                    min="0"
                                    max={maxAvailablePrice}
                                    value={minPrice}
                                    onChange={(e) => {
                                        const val = Math.min(Number(e.target.value), maxPrice);
                                        setMinPrice(val);
                                        setCurrentPage(1);
                                    }}
                                    className={styles.priceSlider}
                                />
                            </div>
                            <div className={styles.priceRangeContainer}>
                                <label htmlFor="maxPrice" className={styles.priceLabel}>
                                    Max Price: <span className={styles.priceValue}>${maxPrice}</span>
                                </label>
                                <input
                                    type="range"
                                    id="maxPrice"
                                    min="0"
                                    max={maxAvailablePrice}
                                    value={maxPrice}
                                    onChange={(e) => {
                                        const val = Math.max(Number(e.target.value), minPrice);
                                        setMaxPrice(val);
                                        setCurrentPage(1);
                                    }}
                                    className={styles.priceSlider}
                                />
                            </div>
                        </div>

                        <div className={styles.filterSection}>
                            <h3 className={styles.filterTitle}>Filter by Brand</h3>
                            <div className={styles.brandList}>
                                {brands.length === 0 && (
                                    <div className={styles.noProducts}>No brands available</div>
                                )}
                                {brands.map((brand) => (
                                    <div key={brand} className={styles.brandItem}>
                                        <input
                                            type="checkbox"
                                            id={`brand-${brand}`}
                                            checked={selectedBrands.includes(brand)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedBrands([...selectedBrands, brand]);
                                                } else {
                                                    setSelectedBrands(selectedBrands.filter(b => b !== brand));
                                                }
                                                setCurrentPage(1);
                                            }}
                                            className={styles.brandCheckbox}
                                        />
                                        <label htmlFor={`brand-${brand}`} className={styles.brandLabel}>
                                            {brand}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className={styles.productsContainer}>
                        {loading ? (
                            <div className={styles.loadingContainer}>
                                <div className={styles.spinner}></div>
                            </div>
                        ) : error ? (
                            <div className={styles.errorMessage}>{error}</div>
                        ) : pagedProducts.length === 0 ? (
                            <div className={styles.noProducts}>
                                No products found in this {isTrolly ? 'section' : size ? 'size' : 'category'}.
                            </div>
                        ) : (
                            <Products
                                products={pagedProducts}
                                onAddToCart={onAddToCart}
                                onItemClick={handleItemClick}
                                cartList={cartList}
                            />
                        )}

                        {totalPages > 1 && (
                            <div className={styles.paginationContainer}>
                                <button 
                                    onClick={() => goToPage(currentPage - 1)} 
                                    disabled={currentPage === 1}
                                    className={styles.paginationButton}
                                >
                                    Previous
                                </button>
                                {[...Array(totalPages)].map((_, i) => (
                                    <button
                                        key={i + 1}
                                        onClick={() => goToPage(i + 1)}
                                        className={`${styles.paginationButton} ${currentPage === i + 1 ? styles.activePage : ''}`}
                                    >
                                        {i + 1}
                                    </button>
                                ))}
                                <button 
                                    onClick={() => goToPage(currentPage + 1)} 
                                    disabled={currentPage === totalPages}
                                    className={styles.paginationButton}
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <ProductModal 
                product={selectedProduct}
                onClose={closeModal}
                onAddToCart={onAddToCart}
            />

            
            <br/>            <br/>
            <br/>
            <br/>
            <br/>
            <br/>
            
            <Footer />
        </>
    );
};

export default CategoryProducts;
